export interface CaseStudyMetric {
  metric: string;
  value: string;
}

export interface CaseStudyOutcome {
  title: string;
  detail: string;
}

export interface CaseStudyTimelineItem {
  phase: string;
  duration: string;
  focus: string;
}

export interface CaseStudyTestimonial {
  quote: string;
  author: string;
  role: string;
}

export interface CaseStudy {
  id: string;
  title: string;
  client: string;
  industry: string;
  description: string;
  challenge: string;
  solution: string;
  results: CaseStudyMetric[];
  technologies: string[];
  image: string;
  logo: string;
  accent: string;
  heroImage: string;
  services: string[];
  keyMetrics: CaseStudyMetric[];
  painPoints: string[];
  strategy: string[];
  outcomes: CaseStudyOutcome[];
  timeline: CaseStudyTimelineItem[];
  testimonial: CaseStudyTestimonial;
}

export const caseStudies: CaseStudy[] = [
  {
    id: 'technoflex-web-modernization',
    title: 'Web Application & Database Modernization',
    client: 'Technoflex',
    industry: 'Manufacturing',
    description: 'Modernizing legacy web application and database infrastructure for a leading electrical conduit manufacturing company using Node.js, React, and MySQL on shared hosting.',
    challenge: 'Outdated legacy website with poor performance, limited scalability, and maintenance challenges affecting online presence for electrical conduit products and services.',
    solution: 'Completely rebuilt the web application using modern Node.js backend, React frontend, and optimized MySQL database, deployed on shared hosting at technoflex.in to showcase their manufacturing capabilities and product catalog.',
    results: [
      { metric: 'Page Load Time', value: '-70%' },
      { metric: 'User Engagement', value: '+85%' },
      { metric: 'Database Performance', value: '+90%' },
      { metric: 'Cost Efficiency', value: '60%' }
    ],
    technologies: ['Node.js', 'React', 'MySQL', 'Express.js', 'JavaScript'],
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=900&q=60',
    logo: 'https://dummyimage.com/80x40/1e40af/ffffff&text=Tech',
    accent: 'from-indigo-500 to-blue-600',
    heroImage: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1200&q=60',
    services: ['Application Engineering', 'Database Modernization', 'Web Development'],
    keyMetrics: [
      { metric: 'Pages migrated', value: '50+' },
      { metric: 'Database queries optimized', value: '200+' },
      { metric: 'Uptime achieved', value: '99.9%' }
    ],
    painPoints: [
      'Legacy codebase with outdated technologies causing slow page loads affecting product catalog visibility',
      'Database performance issues leading to poor user experience for customers browsing electrical conduit products',
      'Limited scalability and difficulty in adding new features for product management and catalog updates',
      'High maintenance costs and security vulnerabilities impacting online business operations'
    ],
    strategy: [
      'Migrated entire application to modern Node.js and React architecture for better performance and maintainability',
      'Optimized MySQL database with proper indexing and query optimization for faster product catalog retrieval',
      'Implemented responsive design for mobile and desktop compatibility to reach customers across all devices',
      'Deployed on shared hosting (technoflex.in) with optimized configurations for cost-effective hosting solution'
    ],
    outcomes: [
      { title: 'Performance Boost', detail: 'Page load times reduced by 70%, significantly improving user experience for customers browsing electrical conduit products and enhancing SEO rankings' },
      { title: 'Scalable Architecture', detail: 'Modern stack enables easy product catalog management, feature additions, and future scalability without major refactoring' },
      { title: 'Cost Optimization', detail: 'Shared hosting deployment at technoflex.in reduced infrastructure costs by 60% while maintaining high performance for manufacturing business operations' }
    ],
    timeline: [
      { phase: 'Analysis & Planning', duration: '2 weeks', focus: 'Legacy code review and migration strategy' },
      { phase: 'Development', duration: '8 weeks', focus: 'Node.js backend, React frontend, and database optimization' },
      { phase: 'Testing & Deployment', duration: '2 weeks', focus: 'Quality assurance and deployment to technoflex.in' }
    ],
    testimonial: {
      quote: 'FirstLogic transformed our outdated website into a modern, fast, and scalable platform. The migration was seamless, and the results exceeded our expectations.',
      author: 'Technoflex Team',
      role: 'Technoflex Management'
    }
  },
  {
    id: 'osop-coding-platform',
    title: 'Web Application & Mobile Apps Development',
    client: 'OSOP Coding',
    industry: 'Education',
    description: 'Comprehensive web application and cross-platform mobile apps (iOS & Android) for technology and programming courses, with internal management portal for employees.',
    challenge: 'Need for a unified platform to deliver online and offline programming courses, manage students across multiple centers, and streamline internal operations for employees.',
    solution: 'Developed a full-stack web application using React and Node.js with MySQL database, native mobile apps for iOS and Android platforms, and an internal management portal for course administration and employee coordination.',
    results: [
      { metric: 'User Engagement', value: '+120%' },
      { metric: 'Course Completion', value: '+85%' },
      { metric: 'Operational Efficiency', value: '+90%' },
      { metric: 'Mobile App Downloads', value: '10K+' }
    ],
    technologies: ['React', 'Node.js', 'MySQL', 'React Native', 'iOS', 'Android', 'Express.js', 'JavaScript'],
    image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=900&q=60',
    logo: 'https://dummyimage.com/80x40/3b82f6/ffffff&text=OSOP',
    accent: 'from-blue-500 to-cyan-600',
    heroImage: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1200&q=60',
    services: ['Application Engineering', 'Mobile Development', 'Web Development', 'Database Design'],
    keyMetrics: [
      { metric: 'Platforms delivered', value: '3' },
      { metric: 'Courses managed', value: '50+' },
      { metric: 'Active users', value: '5K+' }
    ],
    painPoints: [
      'Fragmented systems for online and offline course delivery causing inconsistent user experience',
      'No unified platform to manage students across multiple offline centers and online courses',
      'Manual processes for course management, enrollment, and progress tracking',
      'Lack of mobile access preventing students from learning on-the-go',
      'Inefficient internal operations requiring manual coordination between employees'
    ],
    strategy: [
      'Built responsive web application using React.js for dynamic course delivery and enrollment management',
      'Developed native mobile apps for iOS and Android using React Native for seamless cross-platform experience',
      'Implemented Node.js backend with MySQL database for scalable data management and API services',
      'Created internal management portal for employees to manage courses, students, analytics, and operations',
      'Integrated offline access capabilities in mobile apps for course materials and progress tracking'
    ],
    outcomes: [
      { title: 'Unified Learning Experience', detail: 'Students can seamlessly access courses across web and mobile platforms, with synchronized progress tracking and offline capabilities' },
      { title: 'Operational Excellence', detail: 'Internal management portal streamlined administrative tasks, reducing manual workload by 90% and improving coordination between employees' },
      { title: 'Scalable Growth', detail: 'Platform architecture supports growing number of courses, students, and offline centers with robust performance and data management' }
    ],
    timeline: [
      { phase: 'Planning & Design', duration: '3 weeks', focus: 'Requirements gathering, architecture design, and UI/UX planning' },
      { phase: 'Web Development', duration: '6 weeks', focus: 'React frontend, Node.js backend, and MySQL database implementation' },
      { phase: 'Mobile Development', duration: '8 weeks', focus: 'iOS and Android app development with React Native' },
      { phase: 'Management Portal', duration: '4 weeks', focus: 'Internal portal development and employee management features' },
      { phase: 'Testing & Launch', duration: '3 weeks', focus: 'Quality assurance, deployment, and platform launch' }
    ],
    testimonial: {
      quote: 'FirstLogic delivered a comprehensive solution that transformed how we deliver courses. The web platform and mobile apps have significantly improved student engagement, and the internal portal has streamlined our operations.',
      author: 'OSOP Coding Team',
      role: 'OSOP Coding Management'
    }
  },
  {
    id: 'osop-coding-data-engineering',
    title: 'Data Engineering & Analytics Platform',
    client: 'OSOP Coding',
    industry: 'Education',
    description: 'Centralized data engineering solution for unstructured and semi-structured data with interactive dashboards for marketing and sales team performance tracking.',
    challenge: 'Different teams creating separate CSV files with data, leading to fragmented information and no centralized system for KPIs, making it difficult to track marketing and sales performance.',
    solution: 'Implemented a comprehensive data engineering solution using SQL Data Warehouse, Azure Data Factory for ETL processes, and Power BI for reports and dashboards to centralize data and provide real-time insights.',
    results: [
      { metric: 'Data Processing Time', value: '-75%' },
      { metric: 'Report Generation', value: '+90%' },
      { metric: 'KPI Visibility', value: '+100%' },
      { metric: 'Decision Making Speed', value: '+85%' }
    ],
    technologies: ['SQL', 'Azure Data Factory', 'Power BI', 'Data Warehouse', 'ETL', 'Azure'],
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=900&q=60',
    logo: 'https://dummyimage.com/80x40/3b82f6/ffffff&text=OSOP',
    accent: 'from-purple-500 to-indigo-600',
    heroImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=60',
    services: ['Data Engineering', 'Business Intelligence', 'Data Analytics', 'ETL Development'],
    keyMetrics: [
      { metric: 'Data sources integrated', value: '15+' },
      { metric: 'Dashboards created', value: '12' },
      { metric: 'Daily data processed', value: '50K+ rows' }
    ],
    painPoints: [
      'Different teams creating separate CSV files with data causing data silos and inconsistency',
      'No centralized system for KPIs making it impossible to track overall performance',
      'Manual data compilation processes taking hours to generate reports',
      'Lack of real-time insights affecting marketing and sales decision-making',
      'Unstructured and semi-structured data scattered across multiple files and systems'
    ],
    strategy: [
      'Designed and implemented SQL Data Warehouse to centralize all data from various sources',
      'Built Azure Data Factory pipelines for automated ETL processes to handle unstructured and semi-structured data',
      'Created comprehensive Power BI dashboards for marketing and sales team performance tracking',
      'Established data governance and standardization processes for consistent KPI reporting',
      'Implemented automated data refresh schedules ensuring real-time insights for decision-making'
    ],
    outcomes: [
      { title: 'Centralized Data System', detail: 'All team data now flows into a single SQL Data Warehouse, eliminating CSV file fragmentation and providing a single source of truth' },
      { title: 'Real-Time Performance Tracking', detail: 'Marketing and sales teams have instant access to KPIs through Power BI dashboards, enabling data-driven decision-making' },
      { title: 'Operational Efficiency', detail: 'Automated ETL processes reduced manual data compilation time by 75%, allowing teams to focus on strategic initiatives' }
    ],
    timeline: [
      { phase: 'Data Assessment', duration: '2 weeks', focus: 'Analyze data sources, identify CSV files, and map data structures' },
      { phase: 'Data Warehouse Design', duration: '3 weeks', focus: 'Design SQL Data Warehouse schema and architecture' },
      { phase: 'ETL Development', duration: '4 weeks', focus: 'Build Azure Data Factory pipelines for data ingestion and transformation' },
      { phase: 'Dashboard Development', duration: '3 weeks', focus: 'Create Power BI reports and dashboards for marketing and sales teams' },
      { phase: 'Testing & Deployment', duration: '2 weeks', focus: 'Data validation, performance testing, and production deployment' }
    ],
    testimonial: {
      quote: 'FirstLogic transformed our data chaos into a structured analytics platform. The centralized system and Power BI dashboards have revolutionized how we track performance and make decisions.',
      author: 'OSOP Coding Team',
      role: 'OSOP Coding Management'
    }
  },
  {
    id: 'architek-solutions-usa',
    title: 'Application Enhancement & Donor Management',
    client: 'Architek Solutions USA',
    industry: 'Non-Profit',
    description: 'Application enhancement and development of new donor management features for a USA-based NGO organization, including system modifications to support extended functionality.',
    challenge: 'Existing system required modifications to accommodate new donor management features, ensuring seamless integration without disrupting current operations while extending capabilities for better donor engagement and tracking.',
    solution: 'Enhanced the existing application architecture and developed comprehensive donor management features, including system modifications to seamlessly integrate new functionality with the legacy system while maintaining data integrity and operational continuity.',
    results: [
      { metric: 'Donor Engagement', value: '+65%' },
      { metric: 'System Efficiency', value: '+80%' },
      { metric: 'Feature Adoption', value: '+90%' },
      { metric: 'Data Accuracy', value: '+95%' }
    ],
    technologies: ['React', 'Node.js', 'SQL Server', 'REST API', 'JavaScript', 'TypeScript'],
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=900&q=60',
    logo: 'https://dummyimage.com/80x40/059669/ffffff&text=Architek',
    accent: 'from-emerald-500 to-teal-600',
    heroImage: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=60',
    services: ['Application Engineering', 'System Enhancement', 'Feature Development', 'Legacy Integration'],
    keyMetrics: [
      { metric: 'New features added', value: '15+' },
      { metric: 'System modules enhanced', value: '8' },
      { metric: 'Donor records managed', value: '10K+' }
    ],
    painPoints: [
      'Existing system architecture needed modifications to support new donor management features',
      'Legacy system integration challenges when adding extended functionality',
      'Risk of disrupting current operations while implementing enhancements',
      'Need to maintain data integrity and system stability during feature additions',
      'Complexity of extending donor management capabilities without breaking existing workflows'
    ],
    strategy: [
      'Conducted comprehensive system analysis to identify integration points and modification requirements',
      'Designed modular architecture enhancements to seamlessly integrate new donor management features',
      'Developed extended donor management capabilities including tracking, engagement, and reporting modules',
      'Implemented careful system modifications ensuring backward compatibility with existing functionality',
      'Established robust testing protocols to validate enhancements without disrupting current operations'
    ],
    outcomes: [
      { title: 'Enhanced Donor Management', detail: 'Extended donor management features provide comprehensive tracking, engagement tools, and reporting capabilities, improving donor relationships by 65%' },
      { title: 'Seamless System Integration', detail: 'Successfully modified existing system architecture to support new features while maintaining 100% operational continuity and data integrity' },
      { title: 'Scalable Architecture', detail: 'Modular enhancements enable future feature additions without major system overhauls, supporting long-term organizational growth' }
    ],
    timeline: [
      { phase: 'System Analysis', duration: '2 weeks', focus: 'Analyze existing system architecture and identify modification requirements' },
      { phase: 'Design & Planning', duration: '3 weeks', focus: 'Design enhancement architecture and donor management feature specifications' },
      { phase: 'Development', duration: '8 weeks', focus: 'Develop new features and implement system modifications' },
      { phase: 'Integration & Testing', duration: '3 weeks', focus: 'Integrate enhancements with existing system and comprehensive testing' },
      { phase: 'Deployment & Training', duration: '2 weeks', focus: 'Production deployment and user training for new features' }
    ],
    testimonial: {
      quote: 'FirstLogic expertly enhanced our application and extended our donor management capabilities. The seamless integration and new features have significantly improved our ability to engage and manage donors effectively.',
      author: 'Architek Solutions USA Team',
      role: 'Architek Solutions USA Management'
    }
  }
];

